import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import {
  copyReferralIconSrc,
  referralCodeFrameSrc,
  referralsIllustrationSrc,
} from '../../assets/referrals';
import { BAZAAR_DIALOG_INNER_BG, BAZAAR_DIALOG_INNER_SHADOW } from '../bazzar/bazaarDialogShared';
import { copyReferralCode } from '../../services/referralService';
import { showAppError } from '../../services/messageDialogService';

/** Figma 11147:1547 / 1548 / 1550 — referral invite card */
export default function ReferralInvitePanel({ referral, onReferNow }) {
  const [copied, setCopied] = useState(false);
  const referralCode = referral?.referralCode || '';

  const handleCopyCode = async () => {
    try {
      await copyReferralCode(referralCode);
      setCopied(true);
      toast.success('Referral code copied!');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      showAppError('Unable to copy referral code.');
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 390, mx: 'auto' }}>
      <Box
        sx={{
          borderRadius: '36px',
          border: '1px solid #d6a7ff',
          background: 'linear-gradient(180deg, #8f51fb 0%, #5c1ec8 34.483%)',
          boxShadow: '0 0 16px 6px rgba(183, 89, 255, 0.35)',
          p: '5px',
        }}
      >
        <Box
          sx={{
            borderRadius: '32px',
            background: BAZAAR_DIALOG_INNER_BG,
            boxShadow: `${BAZAAR_DIALOG_INNER_SHADOW}, 0 4px 24px rgba(255, 0, 242, 0.2)`,
            px: { xs: 2, md: 3 },
            py: 3,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 1.5,
            }}
          >
            <Box
              component="img"
              src={referralsIllustrationSrc}
              alt="Refer to friends and family"
              sx={{
                width: { xs: 120, md: 140 },
                height: 'auto',
                maxWidth: '100%',
                display: 'block',
                objectFit: 'contain',
              }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: 20, md: 24 },
              lineHeight: '32px',
              letterSpacing: '0.8px',
              background: 'linear-gradient(270deg, #ff00f2 0%, #822aff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 4px 4px rgba(0, 0, 0, 0.1)',
              mb: 1.5,
            }}
          >
            Refer To Friends & Family
          </Typography>

          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: '0.5px',
              color: '#fff',
              maxWidth: 520,
              mx: 'auto',
              mb: 3,
            }}
          >
            Invite your friends and family with a referral link and earn rewards when they join
            Rapchik Successfully!
          </Typography>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 350,
              height: 64,
              mx: 'auto',
              mb: 2.5,
            }}
          >
            <Box
              component="img"
              src={referralCodeFrameSrc}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'fill',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: 12,
                right: 12,
                top: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                gap: 1,
                borderTop: '2px dashed rgba(255, 255, 255, 0.85)',
                borderBottom: '2px dashed rgba(255, 255, 255, 0.85)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: 14,
                  lineHeight: '20px',
                  letterSpacing: '0.5px',
                  color: '#fff',
                  opacity: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Your Referral code
              </Typography>

              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: 16,
                    lineHeight: '24px',
                    letterSpacing: '0.8px',
                    color: '#ff00f2',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {referralCode || '—'}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCopyCode}
                  aria-label={copied ? 'Copied' : 'Copy referral code'}
                  sx={{
                    width: 16,
                    height: 16,
                    p: 0,
                    border: 0,
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={copyReferralIconSrc}
                    alt=""
                    sx={{ width: 16, height: 16, display: 'block' }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ position: 'relative', width: '100%', maxWidth: 350, mx: 'auto' }}>
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: '8px 0 0',
                borderRadius: '100px',
                background: 'linear-gradient(254.88deg, #670062 1.55%, #33007d 97.93%)',
                boxShadow: '0 0 20px rgba(252, 0, 241, 0.36)',
              }}
            />
            <Box
              component="button"
              type="button"
              onClick={onReferNow}
              sx={{
                position: 'relative',
                width: '100%',
                border: '0.3px solid #fff',
                borderRadius: '100px',
                background: 'linear-gradient(270deg, #ff00f2 0%, #822aff 100%)',
                color: '#fff',
                cursor: 'pointer',
                py: 2,
                px: 3,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 18,
                lineHeight: '22px',
                letterSpacing: 0,
                textTransform: 'uppercase',
              }}
            >
              Refer now
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
